import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import type { BizyChannelOptions } from '../channel';
import type { BizyHttpClient } from '../client';
import type { BizyChatV2Request, BizyChatV2Response } from '../types/api';
import type { BizyActionEvent, BizyCompleteEvent, BizyNodeAppendedEvent, BizyNodeTokenEvent } from '../types/channel';
import type { ApprovalDecision, BizyChatMessage, BizyUserMessageNode, FeedbackType } from '../types/chat';
import type { BizyAssistantMessageNode } from '../types/nodes';
import { BIZY_EMOTION_DEFAULT, bizyChatPaneReducer, initialBizyChatPaneState, type BizyChatPaneAction, type BizyChatPaneState } from './chat-reducer';
import { bakeDocument } from './document-stream';

// Master stream timeout: no inbound event for this long ends the turn.
const STREAM_TIMEOUT_MS = 300_000;
// Grace window for a mid-stream cable disconnect to self-recover (sleeping
// laptop / transient drop) before escalating to the timeout path.
const DISCONNECT_GRACE_MS = 2_000;

// Canonical Bizy chat endpoints (relative to the host client's baseURL).
const CHAT_PATH = '/bizy/chat';
const CHAT_CANCEL_PATH = '/bizy/chat/cancel';

let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

let pendingIdCounter = 0;
function nextPendingRequestId(): string {
	return `pending-${Date.now()}-${pendingIdCounter++}`;
}

/** Analytics metadata emitted on stream completion / failure. */
export interface BizyResponseReceivedMeta {
	success: boolean;
	errorType?: 'stream_timeout' | 'connection_lost' | 'streaming_error' | 'conversation_busy' | 'unknown';
	responseTimeMs?: number;
	emotion?: string;
	toolsUsed?: string[];
	ttftMs?: number;
}

export interface UseBizyChatStateOptions {
	/** Transport for the chat POST + cancel POST. */
	http: BizyHttpClient;
	/** Optional model slug to send with each chat request. */
	model?: string;
	/** Conversation this surface is bound to (undefined = new chat). */
	conversationId?: string;
	/** Initial messages (e.g. a hydrated session). */
	initialMessages?: BizyChatMessage[];
	/** Builds the POST `context` for a send; merge per-message overrides into the host's base context. Defaults to the overrides alone. */
	resolveContext?: (perMessageExtraContext?: Record<string, string>) => Record<string, string>;
	/** Classifies a send error as "another tab/window is busy" (HTTP 409). Defaults to checking `err.response.status === 409`. */
	isConflictError?: (error: unknown) => boolean;
	/** Host hook for `page_action` events (navigation, cache invalidation). */
	onPageAction?: (data: BizyActionEvent['data']) => void;
	/** Fires when the server assigns / changes the conversation id. */
	onConversationIdChange?: (conversationId: string) => void;
	/** Analytics: a user message was sent. */
	onMessageSent?: (meta: { messageLength: number; conversationLength: number; isFirstMessage: boolean }) => void;
	/** Analytics: a turn completed or failed. */
	onResponseReceived?: (meta: BizyResponseReceivedMeta) => void;
	/** Analytics: the user cancelled generation. */
	onGenerationCancelled?: () => void;
	/**
	 * When true, POSTs include `conversation_type: "segment"` so the server
	 * manages the active segment id. Used by the continuous-chat "Today" feed.
	 */
	continuousMode?: boolean;
}

export interface UseBizyChatStateResult extends BizyChatPaneState {
	conversationId: string | undefined;
	sendMessage: (content: string, options?: { extraContext?: Record<string, string>; attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }) => void;
	cancelGeneration: () => string | undefined;
	retryLastMessage: () => void;
	updateMessageFeedback: (messageId: string, feedbackType: FeedbackType) => void;
	updateNodeChoice: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	resetChat: () => void;
	/** Drop a trailing un-persisted assistant bubble (mount self-heal). */
	dropOpenAssistant: () => void;
	/** Replace the message list (hydration / conversation load). */
	loadMessages: (messages: BizyChatMessage[], options?: { conversationId?: string; bizyEmotion?: string }) => void;
	/** Prepend older messages at the head of the list (infinite-scroll history load). Does not reset loading/emotion state. */
	prependMessages: (messages: BizyChatMessage[]) => void;
	/** Channel callbacks + the active-request ref; feed these to the host's transport hook (ActionCable / IPC). */
	channelOptions: BizyChannelOptions & { activeRequestIdRef: React.MutableRefObject<string | undefined> };
}

function defaultIsConflictError(error: unknown): boolean {
	return (error as { response?: { status?: number } } | undefined)?.response?.status === 409;
}

/**
 * Transport-agnostic Bizy chat state machine. Owns the optimistic in-flight
 * bubbles, the per-stream accumulators (seq-ordered token buffers, document
 * nodes), the request-id correlation + cross-tab isolation, the stream
 * timeout/disconnect-grace, and the chat POST + cancel. Channel transport
 * (ActionCable / IPC) and host concerns (persistence, navigation, analytics)
 * are injected.
 */
export function useBizyChatState(options: UseBizyChatStateOptions): UseBizyChatStateResult {
	// Callbacks are read through `optionsRef.current` inside the channel handlers
	// so they always fire the latest host callback without resubscribing.
	const { http } = options;
	const isConflictError = options.isConflictError ?? defaultIsConflictError;

	const [state, dispatch] = useReducer(
		(s: BizyChatPaneState, a: BizyChatPaneAction) => bizyChatPaneReducer(s, a, nextMessageKey),
		undefined,
		() => ({
			...initialBizyChatPaneState,
			messages: options.initialMessages ?? [],
		})
	);

	// Mirror the latest options into a ref so the channel handlers (built once)
	// always call the current callbacks without resubscribing.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	// Mirror state for synchronous reads inside cable callbacks.
	const messagesRef = useRef<BizyChatMessage[]>(state.messages);
	messagesRef.current = state.messages;

	// Conversation id is both read synchronously inside cable callbacks (ref) and
	// surfaced to consumers (state) — many host effects key off it, so it must
	// trigger re-renders. `setConversationId` keeps the two in lockstep.
	const [conversationId, setConversationIdState] = useState<string | undefined>(options.conversationId);
	const conversationIdRef = useRef<string | undefined>(options.conversationId);
	const setConversationId = useCallback((id: string | undefined) => {
		conversationIdRef.current = id;
		setConversationIdState(id);
	}, []);
	const isLoadingRef = useRef(false);
	const activeRequestIdRef = useRef<string | undefined>(undefined);
	const requestGenerationRef = useRef(0);
	const lastUserMessageRef = useRef('');
	const messageSentAtRef = useRef<number | null>(null);

	// Streaming accumulators (high-frequency; kept off the reducer).
	const streamingContentRef = useRef('');
	const documentNodesRef = useRef<BizyAssistantMessageNode[]>([]);
	const documentIndexByIdRef = useRef<Map<number, number>>(new Map());
	const tokensByNodeIdRef = useRef<Map<number, Map<number, string>>>(new Map());
	const streamedAppendedMarkdownRef = useRef(false);

	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const disconnectGraceRef = useRef<ReturnType<typeof setTimeout>>();

	const resetStreamingAccumulators = useCallback(() => {
		documentNodesRef.current = [];
		documentIndexByIdRef.current = new Map();
		tokensByNodeIdRef.current = new Map();
		streamedAppendedMarkdownRef.current = false;
	}, []);

	// ─── Timeout management ──────────────────────────────────────────
	const clearStreamTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const clearDisconnectGrace = useCallback(() => {
		if (disconnectGraceRef.current) {
			clearTimeout(disconnectGraceRef.current);
			disconnectGraceRef.current = undefined;
		}
	}, []);

	const endStream = useCallback(() => {
		isLoadingRef.current = false;
		streamingContentRef.current = '';
		messageSentAtRef.current = null;
		resetStreamingAccumulators();
	}, [resetStreamingAccumulators]);

	const handleTimeout = useCallback(
		(reason: 'stream_timeout' | 'connection_lost' = 'stream_timeout') => {
			if (!isLoadingRef.current) return;
			optionsRef.current.onResponseReceived?.({ success: false, errorType: reason });
			clearStreamTimeout();
			clearDisconnectGrace();
			endStream();
			dispatch({ type: 'timeout' });
		},
		[clearStreamTimeout, clearDisconnectGrace, endStream]
	);

	const resetStreamTimeout = useCallback(() => {
		clearStreamTimeout();
		timeoutRef.current = setTimeout(() => handleTimeout('stream_timeout'), STREAM_TIMEOUT_MS);
	}, [clearStreamTimeout, handleTimeout]);

	useEffect(() => () => clearStreamTimeout(), [clearStreamTimeout]);
	useEffect(() => () => clearDisconnectGrace(), [clearDisconnectGrace]);

	// ─── Channel handlers (own tab) ──────────────────────────────────
	const handleToken = useCallback(
		(content: string) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();
			// Tokens carry the full accumulated text; keep the longest (ignore
			// shorter out-of-order arrivals).
			if (content.length >= streamingContentRef.current.length) {
				streamingContentRef.current = content;
			}
			dispatch({ type: 'token', content: streamingContentRef.current });
		},
		[resetStreamTimeout]
	);

	const handleNodeToken = useCallback(
		(data: BizyNodeTokenEvent['data']) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();

			const { content, node_id, seq } = data;
			if (content === '') return;

			// Collision guard: a non-markdown card already at this node_id wins.
			const idx = documentIndexByIdRef.current.get(node_id);
			if (idx !== undefined && documentNodesRef.current[idx]?.type !== 'markdown') return;

			let nodeTokens = tokensByNodeIdRef.current.get(node_id);
			if (!nodeTokens) {
				nodeTokens = new Map();
				tokensByNodeIdRef.current.set(node_id, nodeTokens);
			}
			nodeTokens.set(seq, content);
			const orderedContent = [...nodeTokens.entries()]
				.sort(([a], [b]) => a - b)
				.map(([, c]) => c)
				.join('');

			if (idx !== undefined) {
				documentNodesRef.current[idx] = { type: 'markdown', payload: { content: orderedContent } };
			} else {
				documentNodesRef.current.push({ type: 'markdown', payload: { content: orderedContent } });
				documentIndexByIdRef.current.set(node_id, documentNodesRef.current.length - 1);
			}

			dispatch({ type: 'assistant-nodes', nodes: [...documentNodesRef.current] });
		},
		[resetStreamTimeout]
	);

	const handleNodeAppended = useCallback(
		(data: BizyNodeAppendedEvent['data']) => {
			resetStreamTimeout();

			const { type, payload, node_id } = data;
			const node = (
				type === 'markdown'
					? { type: 'markdown', payload: { ...(payload ?? {}), content: (payload?.content as string | undefined) ?? '' } }
					: { type, payload: payload ?? {} }
			) as BizyAssistantMessageNode;

			if (node.type === 'markdown' && node.payload.role !== 'working_plan') {
				streamedAppendedMarkdownRef.current = true;
			}

			// Rehydrate the ref from the last assistant message when a late card
			// arrives after `complete` already baked + reset the document.
			if (!isLoadingRef.current && documentNodesRef.current.length === 0) {
				const last = messagesRef.current[messagesRef.current.length - 1];
				if (last?.role === 'assistant' && last.nodes.length) {
					documentNodesRef.current = [...last.nodes];
				}
			}

			documentNodesRef.current.push(node);
			documentIndexByIdRef.current.set(node_id, documentNodesRef.current.length - 1);
			// `merge-nodes` (not `assistant-nodes`) so a late card after `complete`
			// lands on the just-finalized message instead of a fresh bubble.
			dispatch({ type: 'merge-nodes', nodes: [...documentNodesRef.current] });
		},
		[resetStreamTimeout]
	);

	const handleToolCall = useCallback(
		(data: { tool_name: string }) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();
			dispatch({ type: 'tool-call', toolName: data.tool_name });
		},
		[resetStreamTimeout]
	);

	const handleComplete = useCallback(
		(data: BizyCompleteEvent['data']) => {
			// Connector response: the connector tool produced the final answer.
			if (data.connector_response && data.content) {
				resetStreamingAccumulators();
				if (isLoadingRef.current) {
					clearStreamTimeout();
					endStream();
				}
				dispatch({ type: 'connector-response', content: data.content, messageId: data.message_id || undefined });
				return;
			}

			if (!isLoadingRef.current) return;
			clearStreamTimeout();

			if (data.conversation_id) {
				setConversationId(data.conversation_id);
				optionsRef.current.onConversationIdChange?.(data.conversation_id);
			}

			// connector_processing: conductor done, connector still working. Strip
			// the half-answer and keep streaming.
			if (data.connector_processing) {
				streamingContentRef.current = '';
				resetStreamingAccumulators();
				dispatch({ type: 'connector-processing' });
				resetStreamTimeout();
				return;
			}

			const ttftMs = messageSentAtRef.current && data.first_token_ms ? data.first_token_ms : undefined;
			optionsRef.current.onResponseReceived?.({
				success: true,
				responseTimeMs: data.response_time_ms,
				emotion: data.emotion || BIZY_EMOTION_DEFAULT,
				toolsUsed: data.tools_used ?? [],
				ttftMs,
			});

			const baked = bakeDocument(documentNodesRef.current, data.content, streamedAppendedMarkdownRef.current);
			const toolsUsed = data.tools_used?.length ? data.tools_used : undefined;
			endStream();

			dispatch({
				type: 'complete',
				content: data.content,
				messageId: data.message_id || undefined,
				nodes: baked.length > 0 ? baked : undefined,
				toolsUsed,
				emotion: data.emotion || BIZY_EMOTION_DEFAULT,
			});
		},
		[clearStreamTimeout, resetStreamTimeout, resetStreamingAccumulators, endStream]
	);

	const handleError = useCallback(() => {
		if (!isLoadingRef.current) return;
		clearStreamTimeout();
		clearDisconnectGrace();
		endStream();
		optionsRef.current.onResponseReceived?.({ success: false, errorType: 'streaming_error' });
		dispatch({ type: 'error' });
	}, [clearStreamTimeout, clearDisconnectGrace, endStream]);

	const handleCancelled = useCallback(() => {
		if (!isLoadingRef.current) return;
		clearStreamTimeout();
		clearDisconnectGrace();
		endStream();
		dispatch({ type: 'cancelled' });
	}, [clearStreamTimeout, clearDisconnectGrace, endStream]);

	const handleSupportEscalated = useCallback(() => {
		if (!isLoadingRef.current) return;
		clearStreamTimeout();
		clearDisconnectGrace();
		endStream();
		dispatch({ type: 'support-escalated' });
	}, [clearStreamTimeout, clearDisconnectGrace, endStream]);

	const handlePageAction = useCallback(
		(data: BizyActionEvent['data']) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();
			optionsRef.current.onPageAction?.(data);
		},
		[resetStreamTimeout]
	);

	const handleDisconnected = useCallback(() => {
		if (!isLoadingRef.current) return;
		// Give the cable a window to reconnect before escalating.
		clearDisconnectGrace();
		disconnectGraceRef.current = setTimeout(() => {
			disconnectGraceRef.current = undefined;
			if (isLoadingRef.current) handleTimeout('connection_lost');
		}, DISCONNECT_GRACE_MS);
	}, [clearDisconnectGrace, handleTimeout]);

	const handleConnected = useCallback(() => {
		clearDisconnectGrace();
	}, [clearDisconnectGrace]);

	// ─── Cross-tab handlers ──────────────────────────────────────────
	const handleOtherTabComplete = useCallback((data: BizyCompleteEvent['data']) => {
		if (data.connector_processing) return;
		dispatch({ type: 'other-tab-loading', loading: false });
	}, []);
	const handleOtherTabError = useCallback(() => dispatch({ type: 'other-tab-loading', loading: false }), []);
	const handleOtherTabLoading = useCallback(() => dispatch({ type: 'other-tab-loading', loading: true }), []);

	// ─── Send / cancel / retry ───────────────────────────────────────
	const postChat = useCallback(
		(message: string, perMessageExtraContext: Record<string, string> | undefined, generation: number) => {
			const context = optionsRef.current.resolveContext?.(perMessageExtraContext) ?? perMessageExtraContext ?? {};
			const body: BizyChatV2Request = {
				message,
				conversation_id: conversationIdRef.current,
				context,
				model: optionsRef.current.model,
				...(optionsRef.current.continuousMode ? { conversation_type: 'segment' } : {}),
			};
			http
				.post<BizyChatV2Response>(CHAT_PATH, body)
				.then((data) => {
					if (requestGenerationRef.current !== generation) return;
					if (data.conversation_id) {
						setConversationId(data.conversation_id);
						optionsRef.current.onConversationIdChange?.(data.conversation_id);
					}
					if (data.request_id) activeRequestIdRef.current = data.request_id;
				})
				.catch((error) => {
					if (requestGenerationRef.current !== generation) return;
					clearStreamTimeout();
					endStream();
					const busy = isConflictError(error);
					optionsRef.current.onResponseReceived?.({ success: false, errorType: busy ? 'conversation_busy' : 'unknown' });
					dispatch(busy ? { type: 'busy-409' } : { type: 'error' });
				});
		},
		[http, isConflictError, clearStreamTimeout, endStream]
	);

	const sendMessage = useCallback(
		(content: string, sendOptions?: { extraContext?: Record<string, string>; attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }) => {
			optionsRef.current.onMessageSent?.({
				messageLength: content.length,
				conversationLength: messagesRef.current.length,
				isFirstMessage: messagesRef.current.length === 0,
			});

			activeRequestIdRef.current = nextPendingRequestId();
			isLoadingRef.current = true;
			streamingContentRef.current = '';
			resetStreamingAccumulators();
			messageSentAtRef.current = Date.now();
			lastUserMessageRef.current = content;
			requestGenerationRef.current += 1;
			const generation = requestGenerationRef.current;
			resetStreamTimeout();

			dispatch({
				type: 'send',
				message: {
					key: nextMessageKey(),
					content,
					clientId: activeRequestIdRef.current,
					timestamp: new Date(),
					attachmentPreviewUrl: sendOptions?.attachmentPreviewUrl,
					nodes: sendOptions?.userNodes,
				},
			});

			postChat(content, sendOptions?.extraContext, generation);
		},
		[postChat, resetStreamTimeout, resetStreamingAccumulators]
	);

	const retryLastMessage = useCallback(() => {
		if (!lastUserMessageRef.current) return;
		activeRequestIdRef.current = nextPendingRequestId();
		isLoadingRef.current = true;
		streamingContentRef.current = '';
		resetStreamingAccumulators();
		messageSentAtRef.current = Date.now();
		requestGenerationRef.current += 1;
		const generation = requestGenerationRef.current;
		resetStreamTimeout();
		dispatch({ type: 'retry-start' });
		postChat(lastUserMessageRef.current, undefined, generation);
	}, [postChat, resetStreamTimeout, resetStreamingAccumulators]);

	// Drop a trailing un-persisted assistant bubble (e.g. mount self-heal when a
	// cached partial answer wasn't superseded by a refetch).
	const dropOpenAssistant = useCallback(() => {
		dispatch({ type: 'drop-open-assistant' });
	}, []);

	const cancelGeneration = useCallback((): string | undefined => {
		if (!isLoadingRef.current) return undefined;
		requestGenerationRef.current += 1;

		// Restore the text the user had in flight.
		const msgs = messagesRef.current;
		const last = msgs[msgs.length - 1];
		let restoredText: string | undefined;
		if (last?.role === 'assistant' && !last.id) {
			const secondLast = msgs[msgs.length - 2];
			if (secondLast?.role === 'user') restoredText = secondLast.content;
		} else if (last?.role === 'user') {
			restoredText = last.content;
		}
		if (!restoredText) {
			const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
			if (lastUser) restoredText = lastUser.content;
		}

		clearStreamTimeout();
		clearDisconnectGrace();
		const requestId = activeRequestIdRef.current;
		const conversationId = conversationIdRef.current;
		endStream();
		dispatch({ type: 'cancelled' });

		if (requestId) {
			http.post(CHAT_CANCEL_PATH, { request_id: requestId, conversation_id: conversationId }).catch(() => {
				// Best-effort; the UI has already cleared.
			});
		}
		optionsRef.current.onGenerationCancelled?.();
		return restoredText;
	}, [http, clearStreamTimeout, clearDisconnectGrace, endStream]);

	const resetChat = useCallback(() => {
		clearStreamTimeout();
		clearDisconnectGrace();
		activeRequestIdRef.current = undefined;
		setConversationId(undefined);
		requestGenerationRef.current += 1;
		endStream();
		dispatch({ type: 'reset' });
	}, [clearStreamTimeout, clearDisconnectGrace, endStream, setConversationId]);

	const loadMessages = useCallback(
		(messages: BizyChatMessage[], loadOptions?: { conversationId?: string; bizyEmotion?: string }) => {
			clearStreamTimeout();
			clearDisconnectGrace();
			activeRequestIdRef.current = undefined;
			requestGenerationRef.current += 1;
			endStream();
			setConversationId(loadOptions?.conversationId);
			dispatch({ type: 'load', messages, bizyEmotion: loadOptions?.bizyEmotion });
		},
		[clearStreamTimeout, clearDisconnectGrace, endStream, setConversationId]
	);

	const prependMessages = useCallback((messages: BizyChatMessage[]) => {
		dispatch({ type: 'prepend', messages });
	}, []);

	const updateMessageFeedback = useCallback((messageId: string, feedbackType: FeedbackType) => {
		dispatch({ type: 'update-feedback', messageId, feedbackType });
	}, []);

	const updateNodeChoice = useCallback((messageKey: string, cardIndex: number, choice: ApprovalDecision) => {
		dispatch({ type: 'update-node-choice', messageKey, cardIndex, choice });
	}, []);

	const channelOptions = useMemo<UseBizyChatStateResult['channelOptions']>(
		() => ({
			onToken: handleToken,
			onNodeToken: handleNodeToken,
			onNodeAppended: handleNodeAppended,
			onComplete: handleComplete,
			onError: handleError,
			onCancelled: handleCancelled,
			onSupportEscalated: handleSupportEscalated,
			onToolCall: handleToolCall,
			onPageAction: handlePageAction,
			onPreliminaryToken: resetStreamTimeout,
			onConnected: handleConnected,
			onDisconnected: handleDisconnected,
			onOtherTabComplete: handleOtherTabComplete,
			onOtherTabError: handleOtherTabError,
			onOtherTabLoading: handleOtherTabLoading,
			activeRequestIdRef,
		}),
		[
			handleToken,
			handleNodeToken,
			handleNodeAppended,
			handleComplete,
			handleError,
			handleCancelled,
			handleSupportEscalated,
			handleToolCall,
			handlePageAction,
			resetStreamTimeout,
			handleConnected,
			handleDisconnected,
			handleOtherTabComplete,
			handleOtherTabError,
			handleOtherTabLoading,
		]
	);

	return {
		...state,
		conversationId,
		sendMessage,
		cancelGeneration,
		retryLastMessage,
		updateMessageFeedback,
		updateNodeChoice,
		resetChat,
		dropOpenAssistant,
		loadMessages,
		prependMessages,
		channelOptions,
	};
}
